import { useQuery } from '@tanstack/react-query';

import axios from 'utils/axios';
import { downloadCsv } from 'utils/download';
import { Customer, LedgerEntry, Me, Overview, Payout, PayoutsInfo, Statement, StatementSummary, StripeLink } from 'types/partner';

// The dashboard is read-only apart from payouts. Every call carries only the partner token and
// the backend scopes each answer to that partner, so no partner id is ever sent from here.
const BASE = '/api/v1/partner';

const get = async <T>(url: string, params?: Record<string, string | undefined>) => (await axios.get<T>(`${BASE}${url}`, { params })).data;

export const useMe = () => useQuery({ queryKey: ['partner', 'me'], queryFn: () => get<Me>('/me'), staleTime: 5 * 60_000 });

export const useOverview = () => useQuery({ queryKey: ['partner', 'overview'], queryFn: () => get<Overview>('/overview') });

export const useCustomers = () =>
  useQuery({ queryKey: ['partner', 'customers'], queryFn: async () => (await get<{ customers: Customer[] }>('/customers')).customers });

export type CommissionFilters = { period?: string; status?: string };

export const useCommissions = (filters: CommissionFilters) =>
  useQuery({
    queryKey: ['partner', 'commissions', filters],
    queryFn: async () => (await get<{ entries: LedgerEntry[] }>('/commissions', filters)).entries
  });

export const useStatements = () =>
  useQuery({ queryKey: ['partner', 'statements'], queryFn: async () => (await get<{ statements: StatementSummary[] }>('/statements')).statements });

export const useStatement = (period: string) =>
  useQuery({ queryKey: ['partner', 'statement', period], queryFn: () => get<Statement>(`/statements/${period}`), enabled: !!period });

export const usePayouts = () => useQuery({ queryKey: ['partner', 'payouts'], queryFn: () => get<PayoutsInfo>('/payouts') });

const post = async <T>(url: string, body?: unknown) => (await axios.post<T>(`${BASE}${url}`, body)).data;

// Payout actions are for the account owner only; staff get 403 "Only the account owner can manage payouts."

/**
 * A one-time Stripe page for bank and tax details; open `url` in this tab. `country` (ISO-2 bank
 * country) is only needed the first time: without one on file the backend answers 400
 * "Choose the country your bank account is in."
 */
export const startStripeOnboarding = (country?: string) =>
  post<StripeLink>('/payouts/stripe/onboarding', country ? { country } : undefined);

/** Sign-in link to the partner's Stripe Express dashboard (bank account, Stripe payout history). */
export const openStripeDashboard = () => post<StripeLink>('/payouts/stripe/dashboard');

/** Asks for the whole available balance; 400 `{detail}` explains why when it can't be requested. */
export const requestPayout = () => post<Payout>('/payouts/requests');

/** Takes back a payout request that NUWAY hasn't approved yet. */
export const withdrawPayoutRequest = (id: number) => post<Payout>(`/payouts/requests/${id}/withdraw`);

export const downloadCommissionsCsv = (period?: string) =>
  downloadCsv(`${BASE}/commissions.csv`, `commissions${period ? `-${period}` : ''}.csv`, { period });

export const downloadStatementCsv = (period: string) => downloadCsv(`${BASE}/statements/${period}/csv`, `statement-${period}.csv`);
